import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, StudentError } from "../studentClient.js";
import QuizStats from "../../components/QuizStats.js";

interface Question { questionId: string; prompt: string; choices: { key: string; text: string }[]; }
interface BreakdownItem { questionId: string; chosen: string | null; correct: string; isRight: boolean; }
interface StartResp {
  quizId: string; windowDate: string; status: string;
  questions: Question[]; answers: Record<string, string>;
  score?: number; total?: number; breakdown?: BreakdownItem[];
}

export default function QuizRunnerPage() {
  const { quizId = "", windowDate } = useParams();
  // When a windowDate is in the URL we're reviewing a past result (read-only),
  // not taking/resuming the current week's quiz.
  const reviewing = !!windowDate;
  const nav = useNavigate();
  const [data, setData] = useState<StartResp | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState<{ score: number; total: number; breakdown: BreakdownItem[] } | null>(null);
  const saveTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const req = reviewing
      ? api<StartResp>(`/student/quizzes/${quizId}/result?windowDate=${encodeURIComponent(windowDate!)}`)
      : api<StartResp>(`/student/quizzes/${quizId}/start`, { method: "POST" });
    req
      .then((d) => { setData(d); setAnswers(d.answers ?? {}); })
      .catch((e) => setError(e instanceof StudentError ? e.message : reviewing ? "Couldn't load those results" : "Couldn't open the quiz"));
    return () => window.clearTimeout(saveTimer.current);
  }, [quizId, windowDate, reviewing]);

  const submitted = data?.status === "submitted" || !!result;

  function choose(qid: string, key: string) {
    const next = { ...answers, [qid]: key };
    setAnswers(next);
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      api(`/student/quizzes/${quizId}/progress`, { method: "PUT", body: { answers: next } }).catch(() => {});
    }, 800);
  }

  async function submit() {
    if (!confirm("Submit your quiz? You can't change answers after this.")) return;
    setBusy("submit"); setError("");
    try {
      const r = await api<{ score: number; total: number; breakdown: BreakdownItem[] }>(`/student/quizzes/${quizId}/submit`, { method: "POST", body: { answers } });
      setResult(r);
    } catch (e) {
      setError(e instanceof StudentError ? e.message : "Submit failed");
    } finally {
      setBusy("");
    }
  }

  async function saveAndExit() {
    window.clearTimeout(saveTimer.current);
    setBusy("save"); setError("");
    try {
      await api(`/student/quizzes/${quizId}/progress`, { method: "PUT", body: { answers } });
      alert("Saved. Your answers are kept - come back any time before Friday 9:00 PM and pick up where you left off.");
      nav("/portal/quizzes");
    } catch (e) {
      setError(e instanceof StudentError ? e.message : "Couldn't save - check your connection and try again.");
    } finally {
      setBusy("");
    }
  }

  if (error && !data) return <div className="login-err">{error}</div>;
  if (!data) return <p className="subhead">Loading quiz...</p>;

  // Review view (submitted now or previously): show right/wrong using the breakdown.
  const breakdown = result?.breakdown ?? data.breakdown;
  if (submitted && breakdown) {
    const byId = new Map(breakdown.map((b) => [b.questionId, b]));
    const score = result?.score ?? data.score ?? 0;
    const total = result?.total ?? data.total ?? data.questions.length;
    return (
      <>
        <p className="subhead"><Link to="/portal/quizzes">← Quizzes</Link></p>
        <h1>Your result</h1>
        <p className="subhead">Submitted. Here's how you did.</p>
        <QuizStats breakdown={breakdown} score={score} total={total} />
        {data.questions.map((q, i) => {
          const b = byId.get(q.questionId);
          return (
            <div key={q.questionId} className="quiz-q card pad" style={{ marginBottom: 12 }}>
              <div className="qprompt">
                {i + 1}. {q.prompt}{" "}
                {!b?.chosen
                  ? <span className="qmark none" title="Not answered">-</span>
                  : b.isRight
                    ? <span className="qmark ok" title="Correct">✓</span>
                    : <span className="qmark bad" title="Incorrect">✗</span>}
              </div>
              {q.choices.map((c) => {
                const chosen = b?.chosen === c.key;
                const correct = b?.correct === c.key;
                const cls = correct ? " correct" : chosen ? " chosenwrong" : "";
                return (
                  <div key={c.key} className={`quiz-opt${cls}`}>
                    <span>{c.key}. {c.text}</span>
                    {correct && <span className="tag ok">correct</span>}
                    {chosen && !correct && <span className="tag bad">your answer</span>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </>
    );
  }

  // Take/resume view.
  const answeredCount = Object.keys(answers).length;
  return (
    <>
      <p className="subhead"><Link to="/portal/quizzes">← Quizzes</Link></p>
      <h1>Quiz</h1>
      <p className="subhead">Your answers are saved automatically. You can stop and come back any time before Friday 9:00 PM using <strong>Save &amp; resume later</strong>. Submit when you're done. {answeredCount}/{data.questions.length} answered.</p>
      {error && <div className="login-err" style={{ marginBottom: 12 }}>{error}</div>}
      {data.questions.map((q, i) => (
        <div key={q.questionId} className="quiz-q card pad" style={{ marginBottom: 12 }}>
          <div className="qprompt">{i + 1}. {q.prompt}</div>
          {q.choices.map((c) => (
            <label key={c.key} className={`quiz-opt${answers[q.questionId] === c.key ? " sel" : ""}`}>
              <input type="radio" name={q.questionId} checked={answers[q.questionId] === c.key} onChange={() => choose(q.questionId, c.key)} />
              <span>{c.key}. {c.text}</span>
            </label>
          ))}
        </div>
      ))}
      <div style={{ height: 80 }} />
      <div className="quiz-bar">
        <div className="inner">
          <span className="count">{answeredCount}/{data.questions.length} answered</span>
          <button className="rbtn" disabled={!!busy} onClick={saveAndExit}>{busy === "save" ? "Saving..." : "Save & resume later"}</button>
          <button className="rbtn coral" disabled={!!busy} onClick={submit}>{busy === "submit" ? "Submitting..." : "Submit quiz"}</button>
        </div>
      </div>
    </>
  );
}
